// Token Usage page — every authenticated user's own token consumption
// (current month + historical), with extra admin-only views (specific
// user, all users) layered in per useSwaAuth().isAdmin.
//
// CRITICAL (per docs/change_plans/archive/token-visibility.md): a TokenUsageDocument
// has the exact same shape whether it's the still-updating current month or
// a finalized historical archive. This page/its components never branch on
// "live vs. historical" data shape — only on `isFinal` for the "still
// updating" badge, and on NotFoundError for the historical-not-found state.
import { useEffect, useMemo, useState } from 'react';
import {
  Body1,
  Caption1,
  Card,
  MessageBar,
  MessageBarBody,
  Spinner,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useSwaAuth } from '../hooks/useSwaAuth';
import {
  useAdminAllUsers,
  useAdminUserUsage,
  useAvailablePeriods,
  useCurrentUsage,
  useHistoricalUsage,
} from '../hooks/useTokenUsageData';
import { NotFoundError, getErrorMessage } from '../utils/errors';
import { summarizeByUser } from '../services/tokenusage';
import PeriodPicker from '../components/tokenusage/PeriodPicker';
import UsageHeroCard from '../components/tokenusage/UsageHeroCard';
import ModelBreakdownTable from '../components/tokenusage/ModelBreakdownTable';
import UsageChart from '../components/tokenusage/UsageChart';
import AdminUsageControls from '../components/tokenusage/AdminUsageControls';
import type { AdminViewMode } from '../components/tokenusage/AdminUsageControls';
import AllUsersTable from '../components/tokenusage/AllUsersTable';

const useStyles = makeStyles({
  page: {
    padding: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    '@media (max-width: 768px)': { paddingTop: tokens.spacingVerticalS, paddingLeft: 0, paddingRight: 0 },
  },
  header: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  controlsRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  chartCard: { padding: tokens.spacingVerticalM },
  loadingRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
});

function currentPeriod(): { year: number; month: number } {
  const now = new Date();
  // Use UTC to match the server/live current-month contract (see
  // tokenusage.ts's currentUtcPeriodKey and the /mytokenusage header
  // comment): the current period is keyed by UTC calendar month, not the
  // browser's local time zone.
  return { year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 };
}

export default function TokenUsage() {
  const styles = useStyles();
  const { isAdmin } = useSwaAuth();

  // "My usage" data source: current-month (polling) query when the current
  // period is selected, otherwise a point-in-time historical query. This
  // must be created before `nowYear`/`nowMonth` below, since the live
  // current period is derived from its own polled `period` field.
  const currentUsageQuery = useCurrentUsage();

  // The live "current period" tracks the polled current-month document's
  // own `period` field (refreshed on every ~5 min poll) rather than a
  // client clock read captured once at mount — a `useMemo(..., [])` would
  // freeze forever, so a tab left open across a month boundary would get
  // stuck labeling the old month as "current" even after the hero/chart
  // had already rolled over to the new month's data. Falls back to a UTC
  // clock read only until the first fetch resolves.
  const { year: nowYear, month: nowMonth } = useMemo(() => {
    const docPeriod = currentUsageQuery.data?.period;
    if (docPeriod) {
      const [y, m] = docPeriod.split('-').map(Number);
      if (y && m) return { year: y, month: m };
    }
    return currentPeriod();
  }, [currentUsageQuery.data?.period]);

  const [selectedYear, setSelectedYear] = useState(nowYear);
  const [selectedMonth, setSelectedMonth] = useState(nowMonth);
  // Whether the picker should keep following the live current period (vs.
  // a historical period the user explicitly navigated to). Using this
  // (rather than just comparing selectedYear/Month to nowYear/nowMonth)
  // means a month-boundary rollover updates the selection automatically
  // when the user hasn't picked a specific historical period, instead of
  // silently routing the old (now-stale) month through the historical
  // query.
  const [followCurrentPeriod, setFollowCurrentPeriod] = useState(true);

  useEffect(() => {
    if (followCurrentPeriod) {
      setSelectedYear(nowYear);
      setSelectedMonth(nowMonth);
    }
  }, [followCurrentPeriod, nowYear, nowMonth]);

  const isCurrentPeriod = followCurrentPeriod;

  const [adminMode, setAdminMode] = useState<AdminViewMode>('self');
  const [lookupOid, setLookupOid] = useState('');

  const effectiveAdminMode: AdminViewMode = isAdmin ? adminMode : 'self';

  const availablePeriodsQuery = useAvailablePeriods();

  // Only fetch the caller's own historical usage in "self" view — in admin
  // "user"/"all" mode, `ownQuery` is never rendered (see `activeQuery` below),
  // so firing it anyway would be a wasted request against the admin's own
  // OID (and a silently-swallowed 404 if they have no archive for that
  // month). See PR #96968 thread 958254.
  const historicalUsageQuery = useHistoricalUsage(selectedYear, selectedMonth, {
    enabled: !isCurrentPeriod && effectiveAdminMode === 'self',
  });
  const ownQuery = isCurrentPeriod ? currentUsageQuery : historicalUsageQuery;

  const adminUserQuery = useAdminUserUsage(lookupOid || undefined, selectedYear, selectedMonth, {
    enabled: isAdmin && effectiveAdminMode === 'user' && !!lookupOid,
  });
  const adminAllQuery = useAdminAllUsers(selectedYear, selectedMonth, {
    enabled: isAdmin && effectiveAdminMode === 'all',
  });

  // The document driving the hero/model/chart sections, whichever admin mode is active.
  const activeQuery =
    effectiveAdminMode === 'user' ? adminUserQuery : effectiveAdminMode === 'all' ? adminAllQuery : ownQuery;

  const heroTitle =
    effectiveAdminMode === 'user'
      ? `User ${lookupOid || '(none selected)'}`
      : effectiveAdminMode === 'all'
        ? 'Org-wide total'
        : 'My usage';

  const allUsersSummary = useMemo(
    () => (adminAllQuery.data ? summarizeByUser(adminAllQuery.data) : []),
    [adminAllQuery.data],
  );

  const showActiveNotFound = activeQuery.error instanceof NotFoundError;
  const showActiveOtherError = activeQuery.error != null && !(activeQuery.error instanceof NotFoundError);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Title2>Token Usage</Title2>
        <Body1>Track your token consumption for the current and past months.</Body1>
      </div>

      <div className={styles.controlsRow}>
        <PeriodPicker
          year={selectedYear}
          month={selectedMonth}
          nowYear={nowYear}
          nowMonth={nowMonth}
          availablePeriods={availablePeriodsQuery.data?.periods}
          onChange={(y, m) => {
            setSelectedYear(y);
            setSelectedMonth(m);
            setFollowCurrentPeriod(y === nowYear && m === nowMonth);
          }}
        />
      </div>

      {availablePeriodsQuery.data?.partial && (
        <MessageBar intent="warning">
          <MessageBarBody>
            The list of available periods may be incomplete due to a temporary storage issue. Some historical
            months may be missing from the picker above.
          </MessageBarBody>
        </MessageBar>
      )}

      {isAdmin && (
        <AdminUsageControls
          mode={adminMode}
          onModeChange={setAdminMode}
          lookupOid={lookupOid}
          onLookupOid={setLookupOid}
        />
      )}

      {effectiveAdminMode === 'user' && !lookupOid && (
        <MessageBar intent="info">
          <MessageBarBody>Enter a user object ID (OID) above to look up their usage.</MessageBarBody>
        </MessageBar>
      )}

      {activeQuery.isLoading && (effectiveAdminMode !== 'user' || !!lookupOid) && (
        <div className={styles.loadingRow}>
          <Spinner size="small" />
          <Caption1>Loading usage…</Caption1>
        </div>
      )}

      {showActiveNotFound && (
        <MessageBar intent="info">
          <MessageBarBody>
            {isCurrentPeriod
              ? // A 404 for the LIVE current period is an anticipated, reachable
                // state (e.g. a brand-new user or user/org the PR3 refresher
                // hasn't generated a document for yet this month) -- it is not
                // a missing historical archive, so it must not use "archived"
                // wording (see PR #96968 thread 958285).
                `No usage recorded yet for ${heroTitle.toLowerCase()} in this period.`
              : `No archived usage data was found for ${heroTitle.toLowerCase()} in this period.`}
          </MessageBarBody>
        </MessageBar>
      )}

      {showActiveOtherError && (
        <MessageBar intent="error">
          <MessageBarBody>{getErrorMessage(activeQuery.error)}</MessageBarBody>
        </MessageBar>
      )}

      {activeQuery.data && (
        <>
          <UsageHeroCard document={activeQuery.data} title={heroTitle} />

          <div className={styles.section}>
            <Body1><strong>Usage by model</strong></Body1>
            <ModelBreakdownTable
              totalsByModel={activeQuery.data.totalsByModel}
              dailyByModel={activeQuery.data.dailyByModel}
            />
          </div>

          <div className={styles.section}>
            <Body1><strong>Daily usage</strong></Body1>
            <Card className={styles.chartCard}>
              <UsageChart
                dailyByModel={activeQuery.data.dailyByModel}
                totalsByModel={activeQuery.data.totalsByModel}
              />
            </Card>
          </div>
        </>
      )}

      {effectiveAdminMode === 'all' && (
        <div className={styles.section}>
          <Body1><strong>All users</strong></Body1>
          <AllUsersTable
            users={allUsersSummary}
            loading={adminAllQuery.isLoading}
            onSelectUser={(oid) => {
              setAdminMode('user');
              setLookupOid(oid);
            }}
          />
        </div>
      )}
    </div>
  );
}
